import { type B2bCustomer, getB2bConfig, listB2bCustomers } from '@api/b2b';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import FilterDropdown from '@components/ui/Dropdown';
import { Button } from '@mantine/core';
import { PlusIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@hooks/useCurrentUser';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import CreateCustomerModal from './CreateCustomerModal';
import styles from './b2b.module.css';
import { Pill } from './shared';
import { money, termsLabel } from './utils';



/** B2B customers: distributors, retail chains, institutions. Selling channels, never suppliers. */
const CustomersPage: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const isAdmin = currentUser?.role === 'ADMIN';
    const { data: config } = useQuery({ queryKey: ['b2b-config'], queryFn: getB2bConfig });
    const { data: customers = [], isLoading } = useQuery({ queryKey: ['b2b-customers'], queryFn: listB2bCustomers });
    const [segment, setSegment] = useState('all');
    const [search, setSearch] = useState('');
    const [creating, setCreating] = useState(false);

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return customers.filter(
            // Inactive customers are no longer customers: listed only in Settings → B2B Customers.
            (c) => c.status !== 'INACTIVE' && (segment === 'all' || c.segment === segment) && (!needle || c.name.toLowerCase().includes(needle) || (c.gstin ?? '').toLowerCase().includes(needle)),
        );
    }, [customers, segment, search]);

    const columns: DataSheetColumn<B2bCustomer>[] = [
        { key: 'name', header: 'Customer', width: 240, sticky: true, render: (v: string, r) => <span>{v}{r.legalName && r.legalName !== v ? <span className={styles.muted}> · {r.legalName}</span> : null}</span> },
        { key: 'segmentLabel', header: 'Segment', width: 140, render: (v: string | null) => (v ? <Pill label={v} tone="info" /> : '—') },
        { key: 'gstin', header: 'GSTIN', width: 170, render: (v: string | null) => v ?? '—' },
        { key: 'tags', header: 'Tags', width: 160, render: (v: string[]) => (v.length ? v.join(', ') : '—') },
        { key: 'paymentTerms', header: 'Terms', width: 160, render: (v: B2bCustomer['paymentTerms']) => termsLabel(v) },
        { key: 'minOrderValue', header: 'Minimum order', width: 130, align: 'right', render: (v: number) => (v > 0 ? money(v) : '—') },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon="🤝"
                title="B2B Customers"
                subtitle="Distributors, retail chains and institutions who order from you"
                actions={
                    <div className={styles.row}>
                        <Button variant={ButtonVariant.OUTLINED} onClick={() => navigate('/b2b/pricing')}>
                            Price lists & schemes
                        </Button>
                        {isAdmin ? (
                            <Button variant={ButtonVariant.ACTION} leftSection={<PlusIcon size={16} />} onClick={() => setCreating(true)} disabled={!config?.segments.length}>
                                Add customer
                            </Button>
                        ) : null}
                    </div>
                }
            />
            {config && !config.segments.length ? (
                <span className={styles.cardHint}>Add a segment (for example Distributor) under Settings → B2B Customers before adding customers.</span>
            ) : null}
            <DataSheet<B2bCustomer>
                title="Customers"
                subtitle={`${rows.length} customers`}
                data={rows}
                columns={columns}
                getRowKey={(r) => r.customerId}
                isLoading={isLoading}
                enableSearch
                searchPlaceholder="Search name or GSTIN"
                searchValue={search}
                onSearchChange={setSearch}
                onRowClick={(r) => navigate(`/b2b/customers/${encodeURIComponent(r.customerId)}`)}
                toolbarControls={
                    <FilterDropdown
                        value={segment}
                        onChange={setSegment}
                        options={[{ label: 'All segments', value: 'all' }, ...(config?.segments ?? []).map((s) => ({ label: s.label, value: s.key }))]}
                        placeholder="Segment"
                    />
                }
                maxHeight="auto"
                emptyState="No B2B customers yet."
            />
            {creating && config ? <CreateCustomerModal config={config} onClose={() => setCreating(false)} /> : null}
        </div>
    );
};

export default CustomersPage;
